// Home.jsx - The front page, and the page every search ad lands on. It says what
// we buy in the first line, lists the things people actually have in the second,
// and keeps the words a seller types into Google on the page: scrap gold, broken
// jewellery, sterling silver, coins, bullion, dental gold.
//
// What this page must not do is lead with the camera. Filming an opening is how
// we are unusual, not what a seller came for, and a page that opens with it
// answers a question nobody asked before answering "what will you pay me". The
// recording belongs here as one promise among four and as one row in the
// comparison, and nowhere near the headline or the meta description.
//
// The claim is deliberately about what the offer is built from rather than about
// beating anybody. "We pay on verified metal content at the day's spot price,
// and here is every number" is checkable and true. "We pay the most" is a
// promise a competitor could hold us to on a slow week.
//
// The order of the page follows the order of the objections: what is this, what
// does it cost me, what happens to my things, why should I believe you, and only
// then, do it.
//
// LEGEND
//   1. Hero          headline, the drawn balance, and the sample offer
//   2. TrustStrip    the four promises
//   3. Steps         how it works in three
//   4. WhatWeBuy     the categories, drawn
//   5. Comparison    us against the two alternatives, a table that stacks
//   6. Proof         quotes, and the invitation to compare the number
//   7. Prices        the live ticker and the calculators
//   8. Questions     the four objections, answered
//   9. CtaBand

import clsx from "clsx";
import { Link } from "react-router";

import {
  Seo, JsonLd, organisationSchema, localBusinessSchema, webSiteSchema,
  faqSchema, howToSchema, serviceSchema,
} from "../lib/seo.jsx";
import { useAsync, useSpot } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import { money } from "../lib/format.js";
import { HOME_FAQS } from "./faqData.js";
import {
  TrustStrip, Steps, STEPS, FaqList, CtaBand, OfferReceipt, StatRow,
  BuyMarquee, MobileCta, Figure, Quote,
} from "./parts.jsx";
import { KIT } from "../lib/media.js";
import { Enter, Reveal, StaggerBoxed, useParallax } from "../ui/motion.jsx";
import {
  ChainCoil, RingSolitaire, EarringPair, Flatware,
  CoinStack, BarStack, PocketWatch, DentalCrown,
} from "../ui/illustrations.jsx";
import { ArrowRight, ArrowUpRight, Scale, Check, Close } from "../ui/icons.jsx";

////////// 4. WHAT WE BUY //////////

const BUYS = [
  { art: ChainCoil, metal: "Chains", note: "Broken counts. Same rate per gram." },
  { art: RingSolitaire, metal: "Rings", note: "Stones deducted and returned." },
  { art: EarringPair, metal: "Odd earrings", note: "The lone survivor of a pair." },
  { art: Flatware, metal: "Sterling", note: "Flatware is heavier than it looks." },
  { art: CoinStack, metal: "Coins", note: "Pre-1965 US, 90 percent silver." },
  { art: BarStack, metal: "Bars", note: "Bullion and generic rounds." },
  { art: PocketWatch, metal: "Watch cases", note: "The case, not the movement." },
  { art: DentalCrown, metal: "Dental gold", note: "Send it with the tooth attached." },
];

////////// 5. COMPARISON //////////
// Every row is something a person can verify about us in ten seconds on this site.

const COMPARISON = [
  ["Paid on verified metal content, not a flat scrap rate", true, "Sometimes", "Sometimes"],
  ["You see every weight and purity test", true, false, "Sometimes"],
  ["An offer without haggling", true, false, true],
  ["Shipping insured in both directions", true, "n/a", "One way"],
  ["Free return if you decline", true, "n/a", "Sometimes"],
  ["Payment goes out the day you accept", true, true, false],
  // Last on purpose. It is the unusual one, not the important one.
  ["The package is opened on camera", true, false, false],
];

const cell = (value) => {
  if (value === true) return <span className="inline-flex items-center gap-1.5 text-positive font-semibold"><Check size={15} /> Yes</span>;
  if (value === false) return <span className="inline-flex items-center gap-1.5 text-faint"><Close size={15} /> No</span>;
  return <span className="text-caution font-medium">{value}</span>;
};

////////// 6. PROOF //////////

// Ordered so the money is the first thing read. The second one is left in with
// its inconvenient half intact, because a page of unmixed praise is the thing
// nobody believes.
const QUOTES = [
  { text: "The offer was less than the melt number I found online, but they showed me exactly why, and it was still more than the two shops near me.", name: "Marcus D.", place: "Toledo, Ohio" },
  { text: "Declined the offer, got everything back three days later, no charge, no argument. That is the only reason I called them again in March.", name: "Priya S.", place: "Naperville, Illinois" },
  { text: "I sent a box of my mother's things expecting the runaround. They filmed the whole thing and the weights matched what I had written down.", name: "Ellen R.", place: "Warwick, Rhode Island" },
];

////////// UPCOMING SHOWS //////////
// A whole section, and only when there is something in it. A gold show is the
// one time this business is not a mailer: somebody drives to a hotel with their
// mother's jewellery in a bag and wants to be told, to their face, what it is
// worth. So it gets the treatment a page section gets, with the photograph of
// the last one, rather than three lines of text under a heading.
//
// It renders nothing at all when no show is published, and that is deliberate.
// An empty "where to find us" is worse than none: a mail-in business does not
// need to apologise for having no event this month, and a heading over a blank
// grid reads like a page that is broken.
//
// This section is not in the prerendered HTML, because a show lives in the
// database and the prerenderer runs at build time with no database. It appears
// on hydration. `Shows.jsx` carries the same note and the same tradeoff.

const showDates = (days = []) => {
  const dates = days.map((day) => day.date).filter(Boolean).sort();
  if (!dates.length) return "";

  const asDay = (date, opts) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", ...opts });

  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first === last) return asDay(first, { weekday: "long", month: "long", day: "numeric" });

  // Same month reads as "March 14 to 16", different months spell both out. The
  // month repeated twice in one line is the sort of thing nobody can say why
  // they disliked.
  const sameMonth = first.slice(0, 7) === last.slice(0, 7);
  return `${asDay(first, { month: "long", day: "numeric" })} to ${asDay(last, sameMonth ? { day: "numeric" } : { month: "long", day: "numeric" })}`;
};

const showHours = (days = []) => {
  const first = days.find((day) => day.opens && day.closes);
  if (!first) return "";
  const clock = (time) => {
    const [hour, minute] = String(time).split(":").map(Number);
    if (!Number.isFinite(hour)) return time;
    const suffix = hour < 12 ? "am" : "pm";
    const twelve = hour % 12 === 0 ? 12 : hour % 12;
    return minute ? `${twelve}:${String(minute).padStart(2, "0")}${suffix}` : `${twelve}${suffix}`;
  };
  return `${clock(first.opens)} to ${clock(first.closes)}`;
};

function ShowCard({ show, lead }) {
  const where = [show.venue, [show.city, show.state].filter(Boolean).join(", ")].filter(Boolean);
  const hours = showHours(show.days);
  // A lead card with a photograph is already two columns: picture, then words.
  // Without one it would be a short paragraph against half a metre of nothing,
  // so the words split into two columns instead and the row reads as finished.
  const split = lead && !show.cover;

  const what = (
    <>
      <p className="eyebrow">{showDates(show.days)}</p>
      <h3 className={clsx(
        "display text-ink mt-1.5 group-hover:text-bronze-dark transition-colors",
        lead ? "display-md" : "display-sm"
      )}>
        {show.title}
      </h3>
      {where.length > 0 && <p className="text-[14px] text-muted mt-2">{where.join(" · ")}</p>}
      {hours && <p className="hint mt-1">Open {hours}</p>}
    </>
  );

  const more = (
    <>
      {lead && show.summary && (
        <p className={clsx("text-[14.5px] text-ink-soft measure leading-relaxed", split ? "mt-4 sm:mt-0" : "mt-3")}>
          {show.summary}
        </p>
      )}
      <span className={clsx(
        "inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink",
        split && show.summary ? "mt-4" : "mt-4"
      )}>
        Times and address
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </span>
    </>
  );

  return (
    <Link
      to={`/shows/${show.slug}`}
      className={clsx(
        "group flex flex-col bg-paper hover:bg-surface transition-colors h-full",
        lead && show.cover && "sm:flex-row"
      )}
    >
      {/* The photograph is the argument: a table, a set of scales, and somebody
          being shown a number. It is left out rather than replaced when a show
          has no cover, because a placeholder box says less than nothing. */}
      {lead && show.cover && (
        <div className="sm:w-[46%] shrink-0 overflow-hidden bg-surface">
          <img
            src={show.cover}
            alt={show.title}
            loading="lazy"
            className="w-full h-48 sm:h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        </div>
      )}

      <div className={clsx(
        "p-5 sm:p-6 flex flex-col justify-center",
        lead && "sm:p-8",
        split && "sm:grid sm:grid-cols-[1.15fr_1fr] sm:gap-x-12 sm:items-center"
      )}>
        {split ? <div>{what}</div> : what}
        {split ? <div className="flex flex-col items-start">{more}</div> : more}
      </div>
    </Link>
  );
}

function UpcomingShows() {
  const { data } = useAsync((signal) => publicApi.shows(signal), []);
  const shows = (data?.upcoming || []).slice(0, 3);
  if (!shows.length) return null;

  return (
    <section className="band bg-surface/40 border-y border-rule clip-x">
      <div className="shell">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8 md:mb-10">
          <div>
            <p className="eyebrow mb-3">In person</p>
            <h2 className="display display-lg text-ink">
              {shows.length === 1 ? "We will be near you." : "Come and see us."}
            </h2>
            <p className="lede mt-4 measure text-muted">
              Bring what you have. We weigh it and test it in front of you, tell you what it is worth,
              and you decide whether to sell. Nothing to pay, nothing to commit to, and no need to tell
              us you are coming.
            </p>
          </div>
          <Link to="/shows" className="btn btn-quiet">All shows <ArrowRight size={16} /></Link>
        </div>

        {/* One flat array of children, not a card followed by a mapped array.
            StaggerBoxed wraps each child it is given, and an array counts as
            one, so the mapped shows would land inside a single grid cell
            stacked on top of each other. The wrapper is also the grid item,
            which is why the column span is passed to it rather than set on the
            card. */}
        <StaggerBoxed
          as="div"
          step={90}
          className="grid gap-px bg-rule border border-rule sm:grid-cols-2"
          itemClassName={(index) => clsx("bg-paper h-full", index === 0 && "sm:col-span-2")}
        >
          {shows.map((show, index) => (
            <ShowCard key={show.slug} show={show} lead={index === 0} />
          ))}
        </StaggerBoxed>
      </div>
    </section>
  );
}

export default function Home() {
  const { spot } = useSpot();
  const artRef = useParallax(0.05);

  return (
    <>
      <Seo
        path="/"
        description="We buy gold and silver in any form: scrap, broken jewellery, sterling flatware, coins and bullion. Free insured kit, an itemised offer on verified metal content, paid the day you accept."
      />
      {/* The home page carries the entity. Every other page points at the same
          @id rather than describing the business again. */}
      <JsonLd data={organisationSchema()} />
      <JsonLd data={localBusinessSchema()} />
      <JsonLd data={webSiteSchema()} />
      <JsonLd data={serviceSchema({
        name: "Mail-in precious metals buying",
        description: "We buy gold, silver, platinum and palladium from the public in any form, from broken jewellery and scrap to coins and bullion. Free insured mail-in kit, offers based on verified metal content, and free insured returns.",
        path: "/",
      })} />
      <JsonLd data={howToSchema({
        name: "How to sell gold by mail",
        description: "Request a free insured kit, mail your items, review the itemised offer, and get paid.",
        path: "/how-it-works",
        steps: STEPS.map((step) => ({ title: step.title, text: step.text })),
      })} />
      <JsonLd data={faqSchema(HOME_FAQS)} />

      {/* ////////// 1. HERO ////////// */}
      <section className="hero clip-x">
        <div className="shell grid gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-12 xl:gap-16 items-center w-full">
          <div>
            <Enter as="p" from="none" className="eyebrow mb-4 sm:mb-5">
              By mail, nationwide &middot; since 2008
            </Enter>

            <Enter as="h1" delay={70} className="display display-xl text-ink">
              {/* The explicit space matters: JSX drops the leading whitespace
                  inside the span, which glues the two words together. */}
              We buy gold and silver{" "}
              <span className="relative inline-block">
                <span className="relative z-10">in any form.</span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 bottom-[0.06em] h-[0.28em] w-full bg-bronze-soft/30 origin-left"
                  style={{ animation: "grow-underline 0.9s var(--ease-out) 0.6s both" }}
                />
              </span>
            </Enter>

            <Enter as="p" delay={150} className="lede mt-5 sm:mt-7 measure">
              Scrap gold, broken chains, single earrings, class rings, sterling flatware, coins,
              bullion and dental gold. We post you a free insured kit, weigh and test every piece,
              and send you an itemised offer the day it arrives. Accept and you are paid that day.
              Decline and all of it comes back, free.
            </Enter>

            <Enter delay={230} className="flex flex-wrap items-center gap-3 mt-7 sm:mt-8">
              <Link to="/get-a-kit" className="btn btn-primary btn-lg w-full xs:w-auto">
                Get a free kit <ArrowRight size={17} />
              </Link>
              {/* Both buttons take the same width or neither does. One full
                  width bar above one shrink to fit button reads as a mistake. */}
              <Link to="/how-it-works" className="btn btn-outline btn-lg w-full xs:w-auto">See how it works</Link>
            </Enter>

            <Enter as="p" delay={300} className="hint mt-5">
              The kit is free and insured both ways. No obligation, and we pay the return
              shipping if you say no.
            </Enter>
          </div>

          {/* The mailers, and nothing else in this column.

              The sample offer used to sit here, tucked under the art. Two tall
              things in one column meant both were small, the card covered the
              product, and the whole corner read as a collision. The offer has
              moved to the dark band further down, which is the section actually
              arguing about numbers, and it can be read there rather than
              glimpsed here.

              What is left is the thing somebody is deciding whether to order.
              It is our own printed mailer, and the bottom third of every one of
              them is a photograph of the scrap gold we buy, so the picture says
              what we do and what turns up in one go. */}
          <div className="lg:pl-2">
            <Enter from="scale" delay={180}>
              <div
                ref={artRef}
                className="hero-art w-full max-w-[20rem] xs:max-w-[23rem] sm:max-w-[26rem] mx-auto lg:mx-0 lg:ml-auto lg:w-[96%] lg:max-w-none"
              >
                <Figure
                  src={KIT.mailerTilt}
                  alt="Free appraisal mailers, ready to be filled and posted back"
                  sizes="(min-width: 1024px) 44vw, 92vw"
                  imgClassName="drop-shadow-[0_18px_40px_rgba(27,26,23,0.13)]"
                  priority
                />
              </div>
            </Enter>
          </div>
        </div>
      </section>

      <Enter as="p" from="none" delay={520} className="shell hint flex items-center gap-2 pb-8 -mt-2 lg:mt-10">
        <Scale size={16} className="text-bronze shrink-0" />
        Every offer shows the weight, the purity test and the metal price it was worked out from.
      </Enter>

      {/* ////////// 2. PROMISES ////////// */}
      <TrustStrip />

      {/* ////////// 3. STEPS ////////// */}
      <section className="band clip-x">
        <div className="shell">
          <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr] lg:items-end mb-8 md:mb-12">
            <Reveal>
              <p className="eyebrow mb-3">The whole process</p>
              <h2 className="display display-lg text-ink">Three steps, about a week.</h2>
            </Reveal>
            <Reveal delay={120} as="p" className="lede text-muted lg:pb-1">
              Most of the week is the post office. The part that involves us usually takes a day.
            </Reveal>
          </div>
          <Steps />
        </div>
      </section>

      {/* ////////// 3b. IN PERSON ////////// */}
      {/* Only on the page when there is actually a show coming. An empty "where
          to find us" is worse than none, and a mail-in business does not need to
          apologise for having no event this month. */}
      <UpcomingShows />

      {/* ////////// 4. WHAT WE BUY ////////// */}
      <BuyMarquee />

      <section className="band bg-surface/40 border-b border-rule clip-x">
        <div className="shell">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:gap-16 lg:items-end mb-8 md:mb-12">
            <Reveal>
              <p className="eyebrow mb-3">What we buy</p>
              <h2 className="display display-lg text-ink">Any condition, one piece or a box of it.</h2>
            </Reveal>
            <Reveal delay={110}>
              <p className="hint leading-relaxed measure">
                Condition does not matter, because everything is valued on its metal content.
                A snapped chain is worth the same per gram as an unbroken one.
              </p>
              <Link to="/what-we-buy" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-ink underline-grow">
                See the full list <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>

          <StaggerBoxed
            as="ul"
            step={70}
            from="scale"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-rule border border-rule"
            itemClassName="bg-paper h-full"
          >
            {BUYS.map(({ art: Drawing, metal, note }) => (
              <li key={metal} className="p-4 sm:p-5 text-center h-full lift">
                <Drawing className="w-20 sm:w-24 mx-auto" />
                <p className="display display-sm text-ink mt-2">{metal}</p>
                <p className="hint mt-1 leading-snug text-[12.5px]">{note}</p>
              </li>
            ))}
          </StaggerBoxed>
        </div>
      </section>

      {/* ////////// 5. COMPARISON ////////// */}
      <section className="band clip-x">
        <div className="shell">
          <Reveal>
            <p className="eyebrow mb-3">An honest comparison</p>
            <h2 className="display display-lg text-ink max-w-2xl">
              The three ways to sell, side by side.
            </h2>
            <p className="lede mt-4 sm:mt-5 measure-wide text-muted">
              A local shop pays you today and you can look them in the eye. That is worth something.
              Here is where the differences actually are.
            </p>
          </Reveal>

          <Reveal delay={140} className="mt-8 md:mt-10">
            {/* Under 640px every row becomes a small labelled card, which is far
                more usable than a table nobody discovers they can scroll. */}
            <table className="stack-table w-full border-collapse text-[14.5px]">
              <thead>
                <tr>
                  <th className="text-left font-normal text-faint text-[11px] uppercase tracking-[0.1em] pb-3 pr-4 w-[40%]">&nbsp;</th>
                  <th className="text-left pb-3 px-4 border-b-2 border-bronze">
                    <span className="display display-sm text-ink">Us</span>
                  </th>
                  <th className="text-left pb-3 px-4 border-b border-rule">
                    <span className="text-[13px] font-semibold text-muted uppercase tracking-wider">Local shop</span>
                  </th>
                  <th className="text-left pb-3 px-4 border-b border-rule">
                    <span className="text-[13px] font-semibold text-muted uppercase tracking-wider">Other mail-in</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([label, us, shop, other]) => (
                  <tr key={label} className="border-b border-rule-soft">
                    <td data-label="" className="py-3.5 pr-4 text-ink-soft font-medium sm:font-normal">{label}</td>
                    <td data-label="Us" className="py-3.5 px-4 sm:bg-bronze-wash/40">{cell(us)}</td>
                    <td data-label="Local shop" className="py-3.5 px-4">{cell(shop)}</td>
                    <td data-label="Other mail-in" className="py-3.5 px-4">{cell(other)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      {/* ////////// 6. PROOF ////////// */}
      <section className="band bg-ink text-paper relative clip-x">
        <span
          aria-hidden="true"
          className="absolute top-1/2 -left-32 w-[560px] h-[560px] -translate-y-1/2 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgb(198 161 91 / 0.12), transparent 70%)" }}
        />
        <div className="shell relative">
          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:items-center">
            <Reveal>
              <p className="eyebrow mb-3 text-bronze-soft">In their words</p>
              <h2 className="display display-lg max-w-2xl">
                You see every number behind the offer.
              </h2>
              <p className="mt-4 sm:mt-5 text-paper/60 measure-wide leading-relaxed">
                We publish every weight, every purity test and the metal price we worked from,
                including the deductions that make the number smaller. That is what lets you
                put our offer next to anybody else&rsquo;s and see which one is actually bigger.
              </p>
            </Reveal>

            {/* The sample offer, which is what this section is arguing about.
                It was in the hero, where it was half covered and nobody had a
                reason to read it yet. Here it is the evidence for the sentence
                above it. */}
            <Reveal from="right" delay={140}>
              <OfferReceipt className="max-w-md mx-auto lg:mx-0 lg:ml-auto text-ink" />
            </Reveal>
          </div>

          {/* Three across only at lg. At 768 these were 200px columns and a
              two line quote became eight lines of two words. */}
          <StaggerBoxed
            as="div"
            step={110}
            className="grid gap-7 sm:gap-8 lg:grid-cols-3 lg:gap-10 mt-10 md:mt-14"
          >
            {QUOTES.map((quote) => <Quote key={quote.name} {...quote} dark />)}
          </StaggerBoxed>
        </div>
      </section>

      {/* ////////// 7. PRICES ////////// */}
      <section className="band clip-x">
        <div className="shell grid gap-9 lg:grid-cols-2 lg:gap-16 lg:items-center">
          <Reveal>
            <p className="eyebrow mb-3">Today&rsquo;s metal prices</p>
            <h2 className="display display-lg text-ink">
              Work out the metal content yourself.
            </h2>
            <p className="lede mt-4 sm:mt-5 measure text-muted">
              Our calculators show what the pure metal in a piece is worth at today&rsquo;s spot price.
              That is the number every buyer starts from, including us, and it is the only fair way
              to compare two offers.
            </p>
            <div className="flex flex-col xs:flex-row flex-wrap gap-3 mt-6 sm:mt-7">
              <Link to="/tools/gold-calculator" className="btn btn-outline">Scrap gold calculator</Link>
              <Link to="/prices" className="btn btn-quiet">All metal prices</Link>
            </div>
          </Reveal>

          <Reveal from="scale" delay={120}>
            <StatRow
              className="lg:border lg:border-rule"
              items={[
                { label: "Gold", value: spot?.perOunce?.gold ? money(spot.perOunce.gold) : "—", hint: "per troy ounce" },
                { label: "Silver", value: spot?.perOunce?.silver ? money(spot.perOunce.silver) : "—", hint: "per troy ounce" },
                { label: "Platinum", value: spot?.perOunce?.platinum ? money(spot.perOunce.platinum) : "—", hint: "per troy ounce" },
              ]}
            />
          </Reveal>
        </div>
      </section>

      {/* ////////// 8. QUESTIONS ////////// */}
      <section className="band-tight pb-14 md:pb-20">
        <div className="shell grid gap-7 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <Reveal>
            <p className="eyebrow mb-3">Before you ask</p>
            <h2 className="display display-lg text-ink">The questions we hear most.</h2>
            <Link to="/faq" className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-ink underline-grow">
              Read all the questions <ArrowUpRight size={15} />
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <FaqList items={HOME_FAQS} defaultOpen={0} />
          </Reveal>
        </div>
      </section>

      {/* ////////// 9. CLOSE ////////// */}
      <CtaBand />

      <MobileCta />
    </>
  );
}
